import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import logoSrc from '../../assets/innovactive-logo.png';
import logoSombreSrc from '../../assets/innovactive-logo-sombre.png';
import { Menu, X, Bell, User, LogOut } from 'lucide-react';
import { useNotification } from '../../contexts/NotificationContext';
import NotificationCenter from '../notifications/NotificationCenter';
import { useAuth } from '../../contexts/AuthContext';
import CurrencySelector from '../CurrencySelector';
import ThemeSelector from '../ThemeSelector';

const HeaderSimple = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationCenterOpen, setIsNotificationCenterOpen] = useState(false);
  const { user, logout } = useAuth();
  const { unreadCount } = useNotification();
  const isAuthenticated = !!user;

  const handleLogout = () => {
    logout();
    setIsUserMenuOpen(false);
  };

  return (
    <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center">
              <img src={logoSrc} alt="Innovactive360°" width={1200} height={142} className="h-6 w-auto dark:hidden" />
              <img src={logoSombreSrc} alt="Innovactive360°" width={1200} height={142} className="h-6 w-auto hidden dark:block" />
            </Link>
          </div>

          {/* Navigation desktop */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/courses"
              className="text-gray-700 hover:text-primary dark:text-gray-300 dark:hover:text-primary-400 transition-colors duration-200"
            >
              Cours
            </Link>
            <Link
              to="/programs"
              className="text-gray-700 hover:text-primary dark:text-gray-300 dark:hover:text-primary-400 transition-colors duration-200"
            >
              Programmes
            </Link>
            <Link
              to="/reviews"
              className="text-gray-700 hover:text-primary dark:text-gray-300 dark:hover:text-primary-400 transition-colors duration-200"
            >
              Avis
            </Link>
            <Link
              to="/gamification"
              className="text-gray-700 hover:text-primary dark:text-gray-300 dark:hover:text-primary-400 transition-colors duration-200"
            >
              Gamification
            </Link>
          </nav>

          {/* Actions */}
          <div className="flex items-center space-x-4">
            {/* Sélecteur de devise */}
            <CurrencySelector />
            
            {/* Sélecteur de thème */}
            <ThemeSelector />

            {/* Notifications */}
            {isAuthenticated && (
              <button 
                onClick={() => setIsNotificationCenterOpen(true)}
                className="relative p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full text-xs text-white flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>
            )}

            {/* User menu */}
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 p-2 text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white transition-colors duration-200"
                >
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {user.name?.charAt(0) || 'U'}
                    </span>
                  </div>
                  <span className="hidden sm:block text-sm font-medium">
                    {user.name || 'Utilisateur'}
                  </span>
                </button>

                {/* Dropdown menu simplifié */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
                    <Link
                      to="/dashboard"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <User className="h-4 w-4 mr-2" />
                      Tableau de bord
                    </Link>
                    <Link
                      to="/profile"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <User className="h-4 w-4 mr-2" />
                      Mon profil
                    </Link>
                    {(user?.role === 'instructor' || user?.role === 'admin') && (
                      <Link
                        to="/instructor"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        <User className="h-4 w-4 mr-2" />
                        Enseigner
                      </Link>
                    )}
                    <div className="border-t border-gray-200 dark:border-gray-700 my-1"></div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      <LogOut className="h-4 w-4 mr-2" />
                      Déconnexion
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-primary dark:text-gray-300 dark:hover:text-primary-400 transition-colors duration-200"
                >
                  Connexion
                </Link>
                <Link
                  to="/register"
                  className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200"
                >
                  S'inscrire
                </Link>
              </div>
            )}

            {/* Menu mobile button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Menu mobile */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 dark:border-gray-700 py-4">
            <div className="space-y-1">
              <Link
                to="/"
                className="block px-2 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                Accueil
              </Link>
              <Link
                to="/courses"
                className="block px-2 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                Cours
              </Link>
              <Link
                to="/programs"
                className="block px-2 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                Programmes
              </Link>
              <Link
                to="/reviews"
                className="block px-2 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                Avis
              </Link>
              <Link
                to="/gamification"
                className="block px-2 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors duration-200"
                onClick={() => setIsMenuOpen(false)}
              >
                Gamification
              </Link>
              {isAuthenticated && (
                <>
                  <div className="border-t border-gray-100 dark:border-gray-700 my-2" />
                  <Link
                    to="/dashboard"
                    className="block px-2 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors duration-200 font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Tableau de bord
                  </Link>
                  <Link
                    to="/my-courses"
                    className="block px-2 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Mes cours
                  </Link>
                  <Link
                    to="/coaching"
                    className="block px-2 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Coaching
                  </Link>
                  <Link
                    to="/certificates"
                    className="block px-2 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Certificats
                  </Link>
                  <Link
                    to="/chat"
                    className="block px-2 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Messages
                  </Link>
                  <Link
                    to="/notifications"
                    className="block px-2 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Notifications
                  </Link>
                  <Link
                    to="/profile"
                    className="block px-2 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Mon profil
                  </Link>
                  <Link
                    to="/settings"
                    className="block px-2 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Paramètres
                  </Link>
                  {(user?.role === 'instructor' || user?.role === 'admin') && (
                    <Link
                      to="/instructor"
                      className="block px-2 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors duration-200"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Enseigner
                    </Link>
                  )}
                  {user?.role === 'admin' && (
                    <Link
                      to="/admin"
                      className="block px-2 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors duration-200"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Administration
                    </Link>
                  )}
                  <div className="border-t border-gray-100 dark:border-gray-700 my-2" />
                  <button
                    onClick={() => { handleLogout(); setIsMenuOpen(false); }}
                    className="block w-full text-left px-2 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                  >
                    Déconnexion
                  </button>
                </>
              )}
              {!isAuthenticated && (
                <>
                  <div className="border-t border-gray-100 dark:border-gray-700 my-2" />
                  <Link
                    to="/login"
                    className="block px-2 py-2 text-gray-700 hover:text-primary hover:bg-gray-50 rounded-lg transition-colors duration-200"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Connexion
                  </Link>
                  <Link
                    to="/register"
                    className="block px-2 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors duration-200 text-center"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    S'inscrire
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Notification Center */}
      {isNotificationCenterOpen && (
        <NotificationCenter onClose={() => setIsNotificationCenterOpen(false)} />
      )}
    </header>
  );
};

export default HeaderSimple;
